import { AlertResponse } from '@eagle/core-data-types';
import { Card, CardContent, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { FC, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { T_MANY, T_ONE } from '../../constants';
import { makeStyles } from '../../theme';
import { AlertListItemDesktop } from './alert-list-item-desktop';

interface LayoutDesktopProps {
  alerts: AlertResponse[];
  isLoading: boolean;
  paginationComponent?: JSX.Element;
  showFeature: boolean;
  showGroups: boolean;
  showPerson: boolean;
  showThing: boolean;
  smallTable: boolean;
  stickyHeader: boolean;
  showAllButtonComponent?: JSX.Element;
}

const StyledCard = styled(Card)({
  padding: 0,
});

const StyledCardContent = styled(CardContent)({
  padding: '0 !important',
  overflowX: 'auto',
});

interface StyledTableBodyProps {
  isLoading: boolean;
}

const StyledTableBody = styled(TableBody, {
  shouldForwardProp: (prop) => prop !== 'isLoading',
})<StyledTableBodyProps>(({ isLoading }) => ({
  filter: isLoading ? 'blur(1px)' : '',
  opacity: isLoading ? 0.66 : 1,
}));

const StyledTableContainer = styled(TableContainer)({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  minWidth: 'min-content',
  width: '100%',
});

const StyledTable = styled(Table)();
const StyledTableCell = styled(TableCell)();

const useStyles = makeStyles()((theme) => ({
  smallTable: {
    '& .MuiTableCell-sizeSmall': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiTableCell-sizeSmall:first-of-type': {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(2),
    },
    '& .MuiTableCell-sizeSmall:last-child': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(3),
    },
  },
}));

const THING_PERSON_WIDTH = 120;
export const DATE_TIME_WIDTH = 175;

export const AlertsTableLayoutDesktop: FC<LayoutDesktopProps> = ({
  alerts,
  isLoading,
  paginationComponent,
  showFeature,
  showGroups,
  showPerson,
  showThing,
  smallTable,
  stickyHeader,
  showAllButtonComponent,
}): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation(['common', 'terms']);
  const routerLocation = useLocation();

  return (
    <StyledCard>
      <StyledCardContent>
        <StyledTableContainer>
          <StyledTable
            aria-label="sticky table"
            className={smallTable ? classes.smallTable : undefined}
            component="div"
            data-testid="alert-table"
            size="small"
            stickyHeader={stickyHeader}
            sx={{ tableLayout: 'fixed' }}
          >
            <TableHead component="div">
              <TableRow component="div">
                <StyledTableCell component="div" sx={{ width: smallTable ? 'unset' : DATE_TIME_WIDTH }}>{t('common:terms.date-time')}</StyledTableCell>
                <StyledTableCell component="div" sx={{ px: 2 }}>{t('common:terms.alert-type')}</StyledTableCell>
                {showFeature && <StyledTableCell component="div">{t('common:terms.feature')}</StyledTableCell>}
                {showThing && <StyledTableCell component="div" sx={{ width: THING_PERSON_WIDTH }}>{t('terms:thing', { count: T_ONE })}</StyledTableCell>}
                {showPerson && <StyledTableCell component="div" sx={{ width: THING_PERSON_WIDTH }}>{t('terms:person', { count: T_ONE })}</StyledTableCell>}
                <StyledTableCell component="div">{t('common:terms.location')}</StyledTableCell>
                {showGroups && <StyledTableCell component="div">{t('common:terms.group', { count: T_MANY })}</StyledTableCell>}
              </TableRow>
            </TableHead>
            <StyledTableBody component="div" isLoading={isLoading}>
              {alerts.map((alert) => (
                <AlertListItemDesktop
                  key={alert.alertId}
                  alert={alert}
                  alertPath={routerLocation.pathname}
                  showFeature={showFeature}
                  showGroups={showGroups}
                  showPerson={showPerson}
                  showThing={showThing}
                  smallTable={smallTable}
                />
              ))}
            </StyledTableBody>
          </StyledTable>
          {showAllButtonComponent ? <Box display='flex' justifyContent='center' sx={paginationComponent ? { mt: 2 } : { my: 2 }}> {showAllButtonComponent} </Box> : null}
          {paginationComponent
            ? <Box display='flex' justifyContent='center' marginY={1}>
              {paginationComponent}
            </Box>
            : null
          }
        </StyledTableContainer>
      </StyledCardContent>
    </StyledCard>
  );
};
